
import { defineComponent, PropType, ref, watch, onBeforeUnmount } from 'vue'

interface TableColumn {
    key: string
    title: string
    width?: number | string
    fixed?: 'left' | 'right'
    align?: 'left' | 'center' | 'right'
    sortable?: boolean
    formatter?: string
}

export default defineComponent({
    name: 'HeaderSettingsPanel',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        columns: {
            type: Array as PropType<TableColumn[]>,
            default: () => []
        },
        defaultColumns: {
            type: Array as PropType<TableColumn[]>,
            default: () => []
        },
        theme: {
            type: String as PropType<'light' | 'dark'>,
            default: 'dark'
        }
    },
    emits: ['update:visible', 'apply', 'cancel'],
    setup(props, { emit }) {
        const localColumns = ref<TableColumn[]>([])
        const dragIndex = ref<number | null>(null)
        const columnListRef = ref<HTMLElement | null>(null)

        // ghost 克隆元素（跟随鼠标的浮动副本）
        let ghostEl: HTMLElement | null = null
        // 鼠标在被拖拽元素内的偏移
        let offsetX = 0
        let offsetY = 0
        let rafId = 0
        let mouseX = 0
        let mouseY = 0
        // 交换冷却
        let lastSwapTime = 0
        const SWAP_COOLDOWN = 100

        // 缓存所有 tag 的位置信息，避免 pointermove 时反复读 DOM
        interface CachedRect { left: number; right: number; top: number; bottom: number; mx: number; my: number }
        let cachedRects: CachedRect[] = []

        watch(() => props.columns, (newColumns) => {
            localColumns.value = newColumns.map(col => ({ ...col }))
        }, { immediate: true, deep: true })

        watch(() => props.visible, (newVisible) => {
            if (newVisible) {
                localColumns.value = props.columns.map(col => ({ ...col }))
            }
        })

        // 快照所有 tag 的 rect 并预计算边距
        const cacheRects = () => {
            if (!columnListRef.value) { cachedRects = []; return }
            const tags = columnListRef.value.querySelectorAll('.column-tag')
            cachedRects = Array.from(tags).map(tag => {
                const r = tag.getBoundingClientRect()
                return {
                    left: r.left, right: r.right, top: r.top, bottom: r.bottom,
                    mx: r.width * 0.15, my: r.height * 0.15
                }
            })
        }

        // 创建跟随鼠标的 ghost 元素
        const createGhost = (sourceEl: HTMLElement, x: number, y: number) => {
            const rect = sourceEl.getBoundingClientRect()
            offsetX = x - rect.left
            offsetY = y - rect.top

            const ghost = sourceEl.cloneNode(true) as HTMLElement
            Object.assign(ghost.style, {
                position: 'fixed',
                left: rect.left + 'px',
                top: rect.top + 'px',
                width: rect.width + 'px',
                height: rect.height + 'px',
                margin: '0',
                zIndex: '9999',
                pointerEvents: 'none',
                opacity: '0.9',
                borderColor: '#1890ff',
                background: 'rgba(24, 144, 255, 0.1)',
                boxShadow: '0 4px 12px rgba(24, 144, 255, 0.35)',
                cursor: 'move',
                willChange: 'transform',
                textAlign: 'center',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            })
            document.body.appendChild(ghost)
            ghostEl = ghost
                // 记录初始位置用于 transform 计算
                ; (ghost as any)._initLeft = rect.left
                ; (ghost as any)._initTop = rect.top
        }

        const removeGhost = () => {
            if (ghostEl && ghostEl.parentNode) {
                ghostEl.parentNode.removeChild(ghostEl)
            }
            ghostEl = null
        }

        // 用缓存的 rect 做碰撞检测，不读 DOM
        // 鼠标进入边框即触发，不需要到中心
        const findHoverIndex = (x: number, y: number): number | null => {
            if (dragIndex.value === null) return null
            for (let i = 0; i < cachedRects.length; i++) {
                if (i === dragIndex.value) continue
                const c = cachedRects[i]
                if (x >= c.left && x <= c.right &&
                    y >= c.top && y <= c.bottom) {
                    return i
                }
            }
            return null
        }

        // 把 ghost 移动 + 碰撞检测 + 交换全部合并到一个 rAF 里
        const rafTick = () => {
            rafId = 0
            // 1. 移动 ghost（用 transform 代替 left/top，跳过 layout）
            if (ghostEl) {
                const dx = mouseX - offsetX - ((ghostEl as any)._initLeft || 0)
                const dy = mouseY - offsetY - ((ghostEl as any)._initTop || 0)
                ghostEl.style.transform = `translate(${dx}px, ${dy}px)`
            }

            // 2. 碰撞检测 + 交换
            if (dragIndex.value === null) return
            const now = Date.now()
            if (now - lastSwapTime < SWAP_COOLDOWN) return

            const hoverIdx = findHoverIndex(mouseX, mouseY)
            if (hoverIdx === null) return

            lastSwapTime = now
            const arr = localColumns.value
            const tmpCol = arr[dragIndex.value]
            arr[dragIndex.value] = arr[hoverIdx]
            arr[hoverIdx] = tmpCol
            // 触发 Vue 响应式更新
            localColumns.value = arr.slice()
            dragIndex.value = hoverIdx

            // 交换后下一帧重新缓存 DOM 位置，保证碰撞检测准确
            requestAnimationFrame(() => {
                cacheRects()
            })
        }

        const handlePointerMove = (e: PointerEvent) => {
            mouseX = e.clientX
            mouseY = e.clientY
            if (!rafId) {
                rafId = requestAnimationFrame(rafTick)
            }
        }

        const handlePointerUp = () => {
            if (rafId) {
                cancelAnimationFrame(rafId)
                rafId = 0
            }
            dragIndex.value = null
            cachedRects = []
            removeGhost()
            document.removeEventListener('pointermove', handlePointerMove)
            document.removeEventListener('pointerup', handlePointerUp)
        }

        const handlePointerDown = (e: PointerEvent, index: number) => {
            if (e.button !== 0) return
            e.preventDefault()

            dragIndex.value = index
            lastSwapTime = 0

            // 拖拽开始时一次性缓存所有位置
            cacheRects()

            // 获取被点击的元素并创建 ghost
            const target = e.currentTarget as HTMLElement
            createGhost(target, e.clientX, e.clientY)

            document.addEventListener('pointermove', handlePointerMove)
            document.addEventListener('pointerup', handlePointerUp)
        }

        onBeforeUnmount(() => {
            if (rafId) cancelAnimationFrame(rafId)
            removeGhost()
            document.removeEventListener('pointermove', handlePointerMove)
            document.removeEventListener('pointerup', handlePointerUp)
        })

        const handleApply = () => {
            emit('apply', localColumns.value)
            emit('update:visible', false)
        }

        const handleCancel = () => {
            emit('cancel')
            emit('update:visible', false)
        }

        const handleReset = () => {
            localColumns.value = props.defaultColumns.map(col => ({ ...col }))
        }

        return {
            localColumns,
            dragIndex,
            columnListRef,
            handlePointerDown,
            handleApply,
            handleCancel,
            handleReset
        }
    }
})
